import { Controller } from '@hotwired/stimulus'
import { enter, leave } from 'el-transition'
import { useClickOutside } from 'stimulus-use'

export default class extends Controller {
  static targets = ['menu', 'trigger']

  static values = {
    open: { type: Boolean, default: false },
  }

  static instances = new Set()

  connect() {
    if (!this.hasMenuTarget || !this.hasTriggerTarget) return

    this.constructor.instances.add(this)

    useClickOutside(this)

    this.#ensureMenuId()
    this.#setupAria()
    this.#syncFromState()
  }

  clickOutside() {
    this.close()
  }

  close() {
    if (!this.openValue) return

    this.openValue = false
    this.#syncAria()

    leave(this.menuTarget).then(() => {
      this.menuTarget.setAttribute('hidden', 'hidden')
    })
  }

  desconnect() {
    this.instances.delete(this)
  }

  // Optional: Esc key from inside the menu
  // data-action="keydown->button-menu#onMenuKeydown"
  onMenuKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }

  // Optional: keyboard support on the trigger
  // data-action="keydown->button-menu#onTriggerKeydown"
  onTriggerKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        if (this.openValue) return
        event.preventDefault()
        this.open()
        break
      case 'Escape':
        if (!this.openValue) return
        event.preventDefault()
        this.close()
        break
      default:
        break
    }
  }

  open() {
    if (this.openValue) return

    this.#closeOtherMenus()
    this.openValue = true
    this.#syncAria()
    this.menuTarget.removeAttribute('hidden')
    enter(this.menuTarget)
  }

  toggle(event) {
    event?.preventDefault()
    this.openValue ? this.close() : this.open()
  }

  // private:

  #closeOtherMenus() {
    this.constructor.instances.forEach((instance) => {
      if (instance !== this) instance.close()
    })
  }

  #ensureMenuId() {
    if (!this.menuTarget.id) {
      this.menuTarget.id = `menu-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`
    }
  }

  #setupAria() {
    this.triggerTarget.setAttribute('aria-haspopup', 'menu')
    this.triggerTarget.setAttribute('aria-controls', this.menuTarget.id)

    if (!this.menuTarget.getAttribute('role')) {
      this.menuTarget.setAttribute('role', 'menu')
    }

    this.#syncAria()
  }

  #syncAria() {
    this.triggerTarget.setAttribute('aria-expanded', this.openValue ? 'true' : 'false')
  }

  #syncFromState() {
    if (this.openValue) {
      this.menuTarget.removeAttribute('hidden')
      this.triggerTarget.setAttribute('aria-expanded', 'true')
    } else {
      this.menuTarget.setAttribute('hidden', 'hidden')
      this.triggerTarget.setAttribute('aria-expanded', 'false')
    }
  }
}
